import { Link as RouterLink } from 'react-router'

import Link from '@mui/material/Link'
import { useTheme } from '@mui/material/styles'

import useFormatMessage from '@hooks/useFormatMessage'
import LogoDark from '@assets/logo/large-dark.svg'
import LogoPride from '@assets/logo/logo-pride.png'

/**
 * Whether the wordmark has a colourway that can stand on a pale surface. Ours comes in
 * two, ink and white; a white label organisation supplies only the one drawn for the
 * dark app bar, so their surfaces have to stay dark.
 *
 * @returns {boolean} True when the dark wordmark is available.
 */
export function useLogoOnLight() {
  const theme = useTheme()
  return theme.app.default === true
}

/**
 * Logo component
 *
 * @param {boolean} isLink - If true, the logo is wrapped in a link to the home page.
 * @param {boolean} onLight - Ask for the colourway that reads on a pale surface.
 * @param {object} props - Additional props to pass to the img element.
 */
export default function Logo({ isLink = true, onLight = false, ...props }) {
  const theme = useTheme()
  const formatMessage = useFormatMessage()
  const canGoDark = useLogoOnLight()

  const currentMonth = new Date().getMonth() // Months are zero-based (0 = January)
  const isPrideMonth = currentMonth === 5 // June is the 6th month (index 5)
  const logoSrc = isPrideMonth ? LogoPride : onLight && canGoDark ? LogoDark : theme.app.logo.large

  const content = <img src={logoSrc} alt="" aria-hidden="true" style={{ maxHeight: 60, maxWidth: 200 }} {...props} />

  if (isLink) {
    return (
      <Link
        component={RouterLink}
        to="/app"
        aria-label={formatMessage('navigation.home')}
        data-testid="logo-home-link"
        sx={{ display: 'flex', height: '100%', width: '100%' }}
      >
        {content}
      </Link>
    )
  }

  return content
}
